// useReportJob — async DOCX report flow over the useApi seam (US-882 ·
// R-2/R-3/R-4/R-6).
//
// Implements the US-891 contract (sdd/specs/US-891/contract.md): trigger =
// POST /country-data-range/ → 202 { task_id, status, status_url } (plus the
// scorecard body, which this hook ignores — that side is US-879's
// useCountryData); then poll status_url until "ready" (+word_report_url) or
// "error", on a fixed interval with a hard deadline so the loop can never
// run forever (R-3). Neither call opts into the client cache: the trigger
// must reach the backend to (re)enqueue the job — the backend already reuses
// its own cached scorecard — and polling by definition needs fresh state.
//
// This hook is the single re-point spot for the report endpoints (R-6); in
// fixtures mode the same calls resolve from fixtures/countryData.js (trigger,
// US-879) and fixtures/reportJob.js (status), registered below.
//
// `error` is a display-ready string (ApiError detail, the job's own error
// message, or the timeout message) — consumers render it, never parse it.
import { useCallback, useEffect, useRef, useState } from "react";
import useApi from "./useApi";
import endpoints from "../api/endpoints";
// Side effect: registers the report-status contract fixture (static demo /
// backend-less dev — US-883 mechanism).
import "../fixtures/reportJob";

const IDLE = { status: "idle", wordReportUrl: null, error: null };

export const REPORT_TIMEOUT_MESSAGE =
  "The report is taking too long — please try again.";

// Contract suggests polling every 2–3 s; the DOCX job for an already-computed
// scorecard takes seconds, so 60 s is a generous guard rail.
const useReportJob = ({ pollInterval = 2500, timeout = 60000 } = {}) => {
  const api = useApi();
  const [state, setState] = useState(IDLE);
  const runRef = useRef(0); // bumping it invalidates any in-flight run
  const timerRef = useRef(null);
  const lastPayloadRef = useRef(null);

  // Invalidate the active run and its pending timer on unmount — no orphan
  // polling, no setState on an unmounted component (R-3).
  useEffect(
    () => () => {
      runRef.current += 1;
      clearTimeout(timerRef.current);
    },
    []
  );

  const trigger = useCallback(
    async (payload) => {
      lastPayloadRef.current = payload;
      const run = ++runRef.current;
      clearTimeout(timerRef.current);
      setState({ status: "processing", wordReportUrl: null, error: null });

      const fail = (message) =>
        setState({ status: "error", wordReportUrl: null, error: message });

      let accepted;
      try {
        accepted = await api.post(endpoints.countryDataRange.path, payload);
      } catch (error) {
        if (run !== runRef.current) return;
        fail(error?.detail || error?.message || "Report request failed.");
        return;
      }
      if (run !== runRef.current) return;

      const statusUrl = accepted?.status_url;
      if (!statusUrl) {
        // 2xx outside the US-891 contract — surface it, never poll blindly.
        fail("The report could not be scheduled — no status URL returned.");
        return;
      }

      const deadline = Date.now() + timeout;

      // poll() never rejects (its one await is wrapped), so scheduling it
      // from setTimeout cannot leak an unhandled rejection.
      const poll = async () => {
        if (run !== runRef.current) return;
        let report;
        try {
          report = await api.get(statusUrl);
        } catch (error) {
          if (run !== runRef.current) return;
          fail(error?.detail || error?.message || "Report status request failed.");
          return;
        }
        if (run !== runRef.current) return;

        if (report?.status === "ready") {
          if (report.word_report_url) {
            setState({
              status: "ready",
              wordReportUrl: report.word_report_url,
              error: null,
            });
          } else {
            fail("The report finished without a download URL.");
          }
        } else if (report?.status === "error") {
          fail(report.error || "Report generation failed.");
        } else if (Date.now() >= deadline) {
          fail(REPORT_TIMEOUT_MESSAGE);
        } else {
          timerRef.current = setTimeout(poll, pollInterval);
        }
      };

      await poll();
    },
    [api, pollInterval, timeout]
  );

  // Re-run the last trigger (error/timeout recovery — R-4). No-op before the
  // first trigger.
  const retry = useCallback(() => {
    if (lastPayloadRef.current !== null) trigger(lastPayloadRef.current);
  }, [trigger]);

  // Back to idle, cancelling any in-flight run (e.g. the selected country
  // changed and the previous report no longer applies).
  const reset = useCallback(() => {
    runRef.current += 1;
    clearTimeout(timerRef.current);
    setState(IDLE);
  }, []);

  return { ...state, trigger, retry, reset };
};

export default useReportJob;
